<script>
    import flickity from 'vue-flickity';
    import { dispatchWindowEvent, waitForFinalEvent } from "shared/eventBus";
    import { getClosestHorizontalScrollingParent } from "shared/Utilities/objectUtilities";

    export default {
        props: {
            name: {
                type: String,
                default: ""
            },
            auto: {
                type: Boolean,
                default: false
            },
            showArrows: {
                type: Boolean,
                default: true
            },
            showDots: {
                type: Boolean,
                default: true
            },
            bottomArrows: {
                type: Boolean,
                default: false
            },
            wrap: {
                type: Boolean,
                default: true
            },
            groupCells: {
                type: Number,
                default: 1
            },
            cellAlign: {
                type: String,
                default: "center"
            },
            initialIndex: {
                type: Number,
                default: 0
            },
            maxDots: {
                type: Number,
                default: 5
            },
            alternateArrowColor: {
                type: Boolean,
                default: false
            }
        },
        components: {
            flickity
        },
        data() {
            return {
                flickityOptions: {
                    autoPlay: this.auto ? 5000 : false,
                    prevNextButtons: this.showArrows,
                    wrapAround: this.wrap,
                    lazyLoad: true,
                    groupCells: this.groupCells,
                    cellAlign: this.cellAlign,
                    initialIndex: this.initialIndex
                    // any options from Flickity can be used
                },
                scrollParent: {},
                scrollParentScrollX: "",
                scrollParentPosition: "",
                initialized: false,
                dragging: false
            }
        },
        computed: {
            ariaLabel() {
                return `${name} carousel.  Use the left and right arrow keys to move the carousel and select different items.`;
            }
        },
        mounted() {
            this.$refs.flickity.on("settle", this.onChange);
            this.$refs.flickity.on("settle", this.onSettle);
            this.$refs.flickity.on("settle", this.dragEnd);
            this.$refs.flickity.on("dragStart", this.dragStart);
            this.$refs.flickity.on("select", this.onSelect);
            this.$refs.flickity.on("scroll", this.onScroll);
            this.$refs.flickity.on("staticClick", this.onClick);
            this.$refs.flickity.on("pointerDown", this.onPointerDown);
            waitForFinalEvent(initializeCarousel, 300, 'carousel-resize');
            this.$nextTick(() => {
                if(this.$refs.flickity) {
                    this.scrollParent = getClosestHorizontalScrollingParent(this.$refs.flickity.$el);
                }
            });
        },
        methods: {
            next() {
                this.$refs.flickity.next();
            },
            previous() {
                this.$refs.flickity.previous();
            },
            onSelect(index) {
                this.calculatePageDotOffset(index);
                this.$emit("select", index);
            },
            onSettle (index) {
                this.$emit("settle", index);
            },
            onChange (index) {
                this.$emit("change", index);
            },
            onScroll(index) {
                this.$emit("scroll", index);
            },
            dragStart(e) {
                this.dragging = true;
                if(this.scrollParent) {
                    this.scrollParent.classList.add("overflow-x-hidden");
                }
            },
            dragEnd(e) {
                this.dragging = false;
                if(this.scrollParent) {
                    this.scrollParent.classList.remove("overflow-x-hidden");
                }
            },
            remove(el) {
                this.$refs.flickity.remove(el);
            },
            initialize() {
                this.$refs.flickity.rerender();
                this.$nextTick(() => initializeCarousel());
            },
            calculatePageDotOffset(index) {
                let pageCount = this.$refs.flickity.cells().length;
                if(pageCount > this.maxDots) {
                    let dotParent = this.$el.querySelector('.flickity-page-dots'),
                        middleDot = Math.ceil(parseFloat(this.maxDots) / 2),
                        allDotElements = nodeListToArray(dotParent.querySelectorAll(`.dot`)),
                        hideElements = [],
                        fadeElements = [],
                        dotWidth = getElementWidthAndMargin(allDotElements[0]),
                        dotParentCenterOffset = 0,
                        selectedPage = index + 1;

                    if(selectedPage > middleDot) {
                        let amountOfDotsToLeft = selectedPage - middleDot,
                            maxDotsToHideOnLeft = pageCount - this.maxDots,
                            hideLeft = Math.min(amountOfDotsToLeft, maxDotsToHideOnLeft), 
                            hideRight = selectedPage + middleDot,
                            hideLeftElements = nodeListToArray(dotParent.querySelectorAll(`.dot:nth-child(-n+${hideLeft})`)),
                            hideRightElements = nodeListToArray(dotParent.querySelectorAll(`.dot:nth-child(n+${hideRight})`));

                        hideElements = hideLeftElements.concat(hideRightElements);

                        fadeElements = nodeListToArray(dotParent.querySelectorAll(`.dot:nth-child(${hideLeft + 1})`))
                            .concat(nodeListToArray(dotParent.querySelectorAll(`.dot:nth-child(${hideRight - 1}):not(:last-child)`)));
                        
                        dotParentCenterOffset = (hideLeftElements.length - hideRightElements.length) * dotWidth;
                    } else {
                        hideElements = nodeListToArray(dotParent.querySelectorAll(`.dot:nth-child(n+${this.maxDots + 1})`));
                        fadeElements = nodeListToArray(dotParent.querySelectorAll(`.dot:nth-child(${this.maxDots})`));
                        dotParentCenterOffset = dotWidth * hideElements.length * -1;
                    }

                    allDotElements.forEach(el => {
                        el.classList.remove("off");
                        el.classList.remove("fade");
                    });

                    fadeElements.forEach(el => el.classList.add("fade"));
                    hideElements.forEach(el => el.classList.add("off"));

                    dotParent.style.marginRight = dotParentCenterOffset + "px";
                }

                this.initialized = true;
            }
        }
    }

    export function initializeCarousel () {
        dispatchWindowEvent('resize')
    }

    function nodeListToArray(nodeList) {
        return [].slice.call(nodeList);
    }

    function getElementWidthAndMargin(el) {
        if(el) {
            let style = el.currentStyle || window.getComputedStyle(el);
            return parseFloat(el.offsetWidth + parseInt(style.marginLeft) + parseInt(style.marginRight));
        }
        return 0;
    }
</script>

<template>
    <flickity ref="flickity" :options="flickityOptions" class="carousel" :class="{ bottom: bottomArrows, alternatecolor: alternateArrowColor, loading: !initialized, nodots: !showDots, noarrows: !showArrows, 'is-grouped': groupCells > 1  }" 
        :aria-label="ariaLabel">
        <slot :next="next" :previous="previous"></slot>
    </flickity>
</template>

<style lang="less" scoped>
    @import "~less-variables";

    .carousel {
        width: 100%;
        flex: 1;
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        @{deep} {
            .carousel-group {
                width: 100%;
            }

            .flickity-slider {
                top: 0;
                left: 0;
                cursor: -webkit-grab;
                &:active{
                    cursor: -webkit-grabbing;
                }  
            }

            &.full-width {
                .flickity-slider {
                    & > * {
                        width: 100%;
                        padding-left: 0;
                        padding-right: 0;
                    }
                }
            }

            .flickity-viewport {
                width: 100%;
                height: 100% !important;
            }

            .flickity-slider {
                & > * {
                    align-items: stretch;
                    transition: opacity .3s;
                    padding-left: 5px;
                    padding-right: 5px;
                    top: 0;
                    bottom: 0;
                    right: 0;
                    left: 0;
                    width: 100%;

                    &:not(.is-selected) {
                        opacity: 0;
                    }
                }
            }

            .flickity-prev-next-button {
                width: 24px;
                height: 24px;
                top: 40%;

                &.flickity-button {
                    background: transparent;
                }

                .flickity-button-icon {
                    fill: @frontier-green;
                }
            }

            .flickity-page-dots .dot {
                border: 2px solid @black;

                &.is-selected {
                    border-color: @frontier-green;
                    background-color: @frontier-green;
                    opacity: 1
                }
            }
        }

        &.is-grouped {
            @{deep} .flickity-slider {
                & > * {
                    &:not(.is-selected) {
                        opacity: 0;
                    }
                }
            }
        }
        
        &.bottom {
            @{deep} .flickity-prev-next-button {
                position: relative;
                top: 0;
                z-index: 2;

                &.previous {
                    align-self: flex-start;
                    transform: translateY(50%);
                }

                &.next {
                    align-self: flex-end;
                    transform: translateY(-50%);
                }
            }
        }

        &.alternatecolor {
            @{deep} .flickity-prev-next-button {
                .flickity-button-icon {
                    fill: @frontier-white;
                }
            }
        }

        &.noarrows {
            @{deep} {
                .flickity-page-dots {
                    display: flex;
                }

                .flickity-button {
                    display: none;
                }
            }
        }
        

        /* todo when no arrows is needed fix specificity to not override tablet-lg display */
        @{deep} .flickity-page-dots {
            display: none;
        }
    }

    @media @tablet-lg {
        .carousel {
            @{deep} .flickity-page-dots {
                display: flex;
                flex-direction: row;
                bottom: 0;
                width: auto;
                position: relative;
                transition: all .4s;

                .dot {
                    opacity: .4;
                    background: @black;
                    margin: 0 4px;
                    width: 5px;
                    height: 5px;

                    &.fade {
                        opacity: .15;
                    }

                    &.off {
                        opacity: 0;
                    }
                }
            }

            @{deep} .flickity-button {
                display: none;
            }

            &.loading {
                @{deep} .flickity-page-dots {
                    opacity: 0;
                }
            }

            &.nodots {
                @{deep} .flickity-page-dots {
                    display: none;
                }

                &:not(.noarrows) {
                    @{deep} .flickity-button {
                        display: block;
                    }
                }
            }
        }
    }
</style>